import React, { CSSProperties, useEffect, useId, useRef } from 'react';
import { ActiveRedirect } from '../utils/useAppRedirect';
import './studentApps.css';

/**
 * The redirect screen (the comp's `redir` overlay, as the Öğrenci Paneli ships it): a circular wipe
 * grows from the icon that was pressed and covers the window in the app's colour, with the app's
 * mark ringed by a drawing circle and two orbiting dots, "Yönlendiriliyorsun", the app's name, one
 * line on what is being prepared ("Tek oturumla güvenle bağlanıyorsun."), three bouncing dots,
 * "Vazgeç", and in a wide window DigiKoç waving from the corner. The copy is the comp's, per app
 * (utils/studentApps.ts); the look is studentApps.css.
 *
 * It is a modal dialog: focus goes into it, the page behind it is inert (StudentAppsHost), Escape
 * and "Vazgeç" cancel.
 */
const AppRedirect: React.FC<{ redirect: ActiveRedirect; onCancel: () => void }> = ({ redirect, onCancel }) => {
  const { app, phase, origin, peek } = redirect;
  const ids = useId();
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dialog.current?.focus({ preventScroll: true });
  }, []);

  const style = { '--wipe-x': `${origin.x}%`, '--wipe-y': `${origin.y}%` } as CSSProperties;
  return (
    <div
      ref={dialog}
      className="app-redirect"
      data-app={app.key}
      data-phase={phase}
      style={style}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${ids}-label ${ids}-name`}
      aria-describedby={`${ids}-sub`}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
      }}
    >
      <span className="app-redirect__blob app-redirect__blob--a" aria-hidden="true" />
      <span className="app-redirect__blob app-redirect__blob--b" aria-hidden="true" />
      <div className="app-redirect__center">
        <div className="app-redirect__mark" aria-hidden="true">
          <svg className="app-redirect__ring" viewBox="0 0 220 220" aria-hidden="true" focusable="false">
            <circle className="app-redirect__track" cx="110" cy="110" r="100" />
            <circle className="app-redirect__draw" cx="110" cy="110" r="100" />
          </svg>
          <span className="app-redirect__orbit">
            <span className="app-redirect__bead app-redirect__bead--a" />
          </span>
          <span className="app-redirect__orbit app-redirect__orbit--b">
            <span className="app-redirect__bead app-redirect__bead--b" />
          </span>
          <span className="app-redirect__tile">
            <img src={app.mark} width={96} alt="" />
          </span>
        </div>
        <p className="app-redirect__label" id={`${ids}-label`}>Yönlendiriliyorsun</p>
        <p className="app-redirect__name" id={`${ids}-name`}>{app.name}</p>
        <p className="app-redirect__sub" id={`${ids}-sub`}>{app.sub}</p>
        <div className="app-redirect__dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <button type="button" className="app-redirect__cancel" onClick={onCancel}>Vazgeç</button>
      </div>
      {peek && (
        <div className="app-redirect__peek" aria-hidden="true">
          <p className="app-redirect__say">{app.say}</p>
          <img className="app-redirect__dk" src="/app-icons/digikoc-wave.webp" width={150} height={150} alt="" />
        </div>
      )}
    </div>
  );
};

export default AppRedirect;
